import { DefaultSwitchSelectionProducts } from '../TopLevelSelectionProducts'
import { sortByBaseProductType, sortByColor, sortByGridProperties } from '../products/productSorting'
import { findGridByItemNo } from '../products/productUtils'
import { GridProduct, SkinProductOption, SkinProduct } from '../types/productTypes'
import { stableSorted } from '../utils/stableSorted'
import { WiserSwitchesProductItemNo } from './wiserSwitchesProduct'

/**
 * Creates a full denormalized list of skin/grid/orientation combinations to be displayed on the selector screen
 * @param skins
 * @param grids
 */
export function getDenormalizedSkinGridProducts(skins: readonly SkinProduct[], grids: readonly GridProduct[]): readonly SkinProductOption[] {
  const skinGridProducts = skins.reduce<SkinProductOption[]>((all, skin) => {
    const gridProduct = findGridByItemNo(skin.grid, grids)

    // use grid orientation, or default to skin image orientation
    const orientation = gridProduct?.orientation ?? skin.imageOrientation ?? 'Vertical'
    const skinOrientationSupport = skin.orientationSupport ?? skin.imageOrientation

    // if grid supports both orientations, duplicate the products for each orientation
    if (orientation === 'Both') {
      return [
        ...all,
        {
          ...skin,
          gridProduct,
          orientation: 'Vertical'
        },
        {
          ...skin,
          gridProduct,
          orientation: 'Horizontal'
        },
      ]
    }

    // if grid only supports a single orientation, but has an otherOrientation product, add both grids with the skin
    if (gridProduct && gridProduct.otherOrientation && skinOrientationSupport === 'Both') {
      const otherGrid = findGridByItemNo(gridProduct.otherOrientation, grids)

      const otherOrientation = otherGrid.orientation

      if (otherOrientation === 'Both') {
        throw Error('other orientation supports multiple orientations')
      }

      return [
        ...all,
        {
          ...skin,
          gridProduct,
          orientation,
        },
        {
          ...skin,
          gridProduct: otherGrid,
          orientation: otherOrientation,
        },
      ]
    }

    return [
      ...all,
      {
        ...skin,
        gridProduct,
        orientation,
      },
    ]
  }, [])

  // Special case: Add 'Wiser Switches' product to Wiser
  const defaultSwitchProduct = skinGridProducts.find((skinProduct) => DefaultSwitchSelectionProducts.includes(skinProduct.itemNo))

  if (defaultSwitchProduct !== undefined) {
    const wiserSwitchesProduct: SkinProductOption = {
      itemNo: WiserSwitchesProductItemNo,
      grid: null,

      color: null,
      description: '',
      displayName: 'Wiser Switches',
      image: defaultSwitchProduct.image,
      imageOrientation: 'Vertical',
      imageScale: null,
      includedRockerItemNo: null,
      style: null,
      type: 'Switch',
      displaySubcategory: 'Switches',

      orientationSupport: undefined,

      gridProduct: null,
      orientation: 'Vertical',

      menuCategories: ['Wiser'],
    }

    skinGridProducts.push(wiserSwitchesProduct)
  }

  // stable sort by color, switches/sockets, then default order defined in spreadsheet
  return stableSorted(
    skinGridProducts,
    (a, b) => {
      // Special case: stable sort Outdoor by type ONLY
      if (a.style === 'Outdoor' && b.style === 'Outdoor') {
        return sortByBaseProductType(a, b)
      }

      return sortByBaseProductType(a, b)
      || sortByColor(a, b)
      || sortByGridProperties(a, b)
      || 0
    }
  )
}
